import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { countryName } from "@/lib/utils_gg";
import { Star } from "lucide-react";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MON_DE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export const compactDate = (e, lang) => {
  const mon = lang === "de" ? MON_DE : MON;
  const [, sm, sd] = e.start_date.split("-");
  const end = e.end_date && e.end_date !== e.start_date ? e.end_date.split("-") : null;
  if (!end) return `${sd} ${mon[+sm - 1]}`;
  return end[1] === sm ? `${sd}–${end[2]} ${mon[+sm - 1]}` : `${sd} ${mon[+sm - 1]} – ${end[2]} ${mon[+end[1] - 1]}`;
};

export default function EventCompactList({ events }) {
  const { t, lang } = useApp();
  const groups = useMemo(() => { const g = {}; events.forEach(e => { const key = e.start_date.slice(0, 7); (g[key] = g[key] || []).push(e); }); return g; }, [events]);
  if (!events.length) return <div className="text-center py-20 border border-dashed border-foreground" data-testid="empty-state">{t("noResults")}</div>;
  return (
    <div data-testid="compact-view">
      {Object.keys(groups).sort().map(k => {
        const [y, m] = k.split("-");
        return (
          <section key={k} className="mb-8">
            <h2 className="gg-month-header sticky top-14 md:top-16 bg-background z-10 !mt-0 !mb-1 !text-2xl md:!text-3xl py-2" data-testid={`compact-month-${k}`}>{t("monthHeaders")[parseInt(m, 10) - 1]} {y}</h2>
            <ul className="divide-y divide-foreground/15 border-b border-foreground/15">
              {groups[k].map(e => {
                const status = ["cancelled", "postponed", "sold_out"].includes(e.public_status) ? e.public_status : null;
                const tba = status === "postponed" && e.new_date_tba;
                return (
                  <li key={e.event_id}>
                    <Link to={`/events/${e.slug}`} className={`group flex flex-wrap sm:flex-nowrap items-baseline gap-x-3 gap-y-0 py-2.5 hover:bg-muted/60 transition-colors -mx-2 px-2 ${status === "cancelled" ? "text-muted-foreground" : ""}`} data-testid={`compact-row-${e.slug}`}>
                      <span className="font-display text-primary font-semibold tracking-wide text-sm w-[5.5rem] sm:w-[6.5rem] shrink-0 tabular-nums" data-testid={`compact-date-${e.slug}`}>{tba ? t("newDateTba") : compactDate(e, lang)}</span>
                      <span className={`font-display uppercase tracking-tight text-base md:text-lg leading-tight min-w-0 flex-1 ${status === "cancelled" ? "line-through decoration-1" : "group-hover:text-primary transition-colors"}`} data-testid={`compact-title-${e.slug}`}>{e.name}</span>
                      <span className="text-sm text-muted-foreground basis-full sm:basis-auto pl-[6.25rem] sm:pl-0 sm:w-[16rem] sm:text-right truncate" data-testid={`compact-place-${e.slug}`}>{e.city}, {countryName(e.country, lang)}</span>
                      {e.is_featured && <span className="eyebrow !text-[9px] px-1.5 py-0.5 shrink-0 gg-recommends text-foreground self-center inline-flex items-center gap-1" data-testid={`compact-featured-${e.slug}`}><Star className="w-2.5 h-2.5 fill-current" />{t("featured")}</span>}
                      {status && <span className={`eyebrow !text-[10px] px-1.5 py-0.5 shrink-0 self-center ${status === "sold_out" ? "border border-foreground" : `gg-status-${status}`}`} data-testid={`compact-status-${e.slug}`}>{t(`public.${status}`)}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
